import { forwardRef, useState, useCallback, type ButtonHTMLAttributes, type MouseEvent } from 'react'
import { motion, AnimatePresence, type HTMLMotionProps } from 'framer-motion'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface RippleEffect {
  id: number
  x: number
  y: number
  size: number
}

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  variant?: ButtonVariant
  size?: ButtonSize
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  enableRipple?: boolean
}

const variants: Record<ButtonVariant, string> = {
  primary: `
    bg-gradient-to-r from-primary-accent to-primary-glow
    text-white font-semibold
    shadow-glow hover:shadow-glow-lg
    border-none
  `,
  secondary: `
    bg-primary-mid/60 backdrop-blur-sm
    text-neutral-100
    border border-white/10
    hover:bg-primary-mid/80 hover:border-white/20
  `,
  ghost: `
    bg-transparent
    text-neutral-300 hover:text-neutral-100
    hover:bg-white/5
    border-none
  `,
  outline: `
    bg-transparent
    text-primary-accent
    border border-primary-accent/50
    hover:bg-primary-accent/10 hover:border-primary-accent
  `,
  danger: `
    bg-secondary-pink/20
    text-secondary-pink
    border border-secondary-pink/30
    hover:bg-secondary-pink/30 hover:border-secondary-pink/50
  `,
}

const sizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-lg',
  md: 'px-4 py-2 text-base rounded-xl',
  lg: 'px-6 py-3 text-lg rounded-xl',
}

const rippleColors: Record<ButtonVariant, string> = {
  primary: 'bg-white/30',
  secondary: 'bg-white/20',
  ghost: 'bg-white/10',
  outline: 'bg-primary-accent/20',
  danger: 'bg-secondary-pink/20',
}

let rippleId = 0

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled,
      leftIcon,
      rightIcon,
      enableRipple = true,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading
    const [ripples, setRipples] = useState<RippleEffect[]>([])

    const handleClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        if (enableRipple && !isDisabled) {
          const button = e.currentTarget
          const rect = button.getBoundingClientRect()
          const size = Math.max(rect.width, rect.height) * 2
          const x = e.clientX - rect.left - size / 2
          const y = e.clientY - rect.top - size / 2

          const newRipple: RippleEffect = {
            id: ++rippleId,
            x,
            y,
            size,
          }

          setRipples((prev) => [...prev, newRipple])

          // Clean up ripple after animation
          setTimeout(() => {
            setRipples((prev) => prev.filter((r) => r.id !== newRipple.id))
          }, 600)
        }

        onClick?.(e)
      },
      [enableRipple, isDisabled, onClick]
    )

    return (
      <motion.button
        ref={ref}
        className={cn(
          'relative inline-flex items-center justify-center gap-2 overflow-hidden',
          'font-medium transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary-deep',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variants[variant],
          sizes[size],
          className
        )}
        disabled={isDisabled}
        whileHover={isDisabled ? undefined : { scale: 1.02 }}
        whileTap={isDisabled ? undefined : { scale: 0.98 }}
        onClick={handleClick}
        {...(props as HTMLMotionProps<'button'>)}
      >
        {/* Ripple effects */}
        <AnimatePresence>
          {ripples.map((ripple) => (
            <motion.span
              key={ripple.id}
              className={cn('absolute rounded-full pointer-events-none', rippleColors[variant])}
              style={{
                left: ripple.x,
                top: ripple.y,
                width: ripple.size,
                height: ripple.size,
              }}
              initial={{ scale: 0, opacity: 0.5 }}
              animate={{ scale: 1, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          ))}
        </AnimatePresence>

        {/* Content */}
        <span className="relative z-10 inline-flex items-center justify-center gap-2">
          {isLoading ? (
            <motion.span
              className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
          ) : (
            leftIcon
          )}
          {children}
          {!isLoading && rightIcon}
        </span>
      </motion.button>
    )
  }
)

Button.displayName = 'Button'
